/* eslint-disable react-hooks/exhaustive-deps */
import { makeStyles } from '@mui/styles';
import { Box, Sidebar } from 'apollo-react';
import NavigationBar from 'apollo-react/components/NavigationBar';
import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { prepareMenuItems } from './prepareMenuItems';
import IQVIALogo from '../../assets/icons/iqvia_core.svg';
import { PILGRIM_TOP_MENU_HEIGHT, SIDEBAR_WIDTH } from '../../constants/SizeConstants';
import FormHistoryDialog from '../../containers/FormHistoryDialog/FormHistoryDialogComponent';
import { useTranslation } from '../../custom-hooks/useTranslation';
import SearchBar from '../../form-designer/components/editor/SearchBar';
import { getNetworkStatus } from '../../form-designer/selectors/common.selectors';
import { getIsRtlLanguage } from '../../selectors/AuthSelectors';
import { getHasManageRight, getHasSupervisorRight } from '../../selectors/SmartInsightSelectors';
import { getUITA } from '../../utils/commonUtils';

const sx = {
  offline: {
    height: 24,
    borderRadius: 16,
    backgroundColor: '#ff9300',
    textAlign: 'center',
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontWeight: 600,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 1.9,
    letterSpacing: 'normal',
    color: '#ffffff',
    padding: '0 18px',
    whiteSpace: 'nowrap',
  },
};

const useStyles = makeStyles({
  sidebar: {
    '& > div > div > div > button': {
      width: '100%',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    '& > div > div > div > button > p': {
      display: 'block',
      fontSize: '9px',
      marginBottom: '0px',
      textAlign: 'center',
      wordBreak: 'break-word',
      width: '100%',
      lineHeight: '100%',
    },
    // This hides the button that opens the sidebar
    '& > div > div > div:nth-of-type(3)': {
      display: 'none',
    },
    // This ensures the div containing the buttons takes up the full height
    '& > div > div > div:nth-of-type(2)': {
      height: '100%',
    },
    '& > div > div > div:nth-of-type(2) > button': {
      padding: 0,
      width: '100%',
    },
  },
  sidebarRTL: {
    '& > div > div': {
      right: 0,
      left: 'auto',
    },
  },
  default: {
    paddingLeft: SIDEBAR_WIDTH,
    paddingTop: PILGRIM_TOP_MENU_HEIGHT,
  },
  defaultRtl: {
    paddingTop: PILGRIM_TOP_MENU_HEIGHT,
    paddingRight: SIDEBAR_WIDTH,
  },
  navigationBar: {
    '& > header > div > div:nth-of-type(1)': {
      marginLeft: 0,
    },
  },
});

const StandaloneLayout = ({ dashboardLink, showSearchBar, children }) => {
  const history = useHistory();
  const classes = useStyles();
  const translateString = useTranslation();

  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const hasSupervisorRight = useSelector(getHasSupervisorRight);
  const hasManageRight = useSelector(getHasManageRight);
  const isOnline = useSelector(getNetworkStatus);

  const menuItems = useMemo(
    () =>
      prepareMenuItems(dashboardLink, hasSupervisorRight, hasManageRight, history, translateString),
    [dashboardLink, hasSupervisorRight, hasManageRight, history]
  );

  useEffect(() => {
    if (isRtlLanguage) {
      document.getElementsByTagName('html')[0].setAttribute('dir', 'rtl');
    } else {
      document.getElementsByTagName('html')[0].removeAttribute('dir');
    }
  }, [isRtlLanguage]);

  const logoPropsSidebar = {
    orgName: 'IQVIA',
    appName: 'App Name',
    onClick: () => history.push('/'),
  };

  const logoProps = {
    src: IQVIALogo,
    alt: 'IQVIA Logo',
    onClick: () => history.push(dashboardLink),
  };

  return (
    <div className={classes.navigationBar} ui-ta={getUITA('Standalone Layout')}>
      <NavigationBar
        variant="brand"
        logoProps={logoProps}
        otherButtons={
          <Box display="flex" alignItems="center">
            {!isOnline && <Box sx={sx.offline}>{translateString('Offline')}</Box>}
            {showSearchBar && <SearchBar />}
          </Box>
        }
      />
      <FormHistoryDialog />
      <div className={classNames(classes.sidebar, isRtlLanguage && classes.sidebarRTL)}>
        <Sidebar
          collapsed
          variant="brand"
          menuItems={menuItems}
          logoComponentProps={logoPropsSidebar}
        />
      </div>
      <div className={isRtlLanguage ? classes.defaultRtl : classes.default}>{children}</div>
    </div>
  );
};

export default StandaloneLayout;
